// frontend/src/pages/SubscriptionSettingsPage.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Divider,
  Button,
  LinearProgress,
  Chip,
  Alert,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import API from "../utils/api";
import useAuth from "../hooks/useAuth";
import { toast } from "react-toastify";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PeopleIcon from "@mui/icons-material/People";
import PaymentIcon from "@mui/icons-material/Payment";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PsychologyIcon from "@mui/icons-material/Psychology";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const CHECKOUT_POLL_MS = 3000;
const CHECKOUT_POLL_MAX_MS = 30000;

export default function SubscriptionSettingsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutHandledRef = useRef(false);
  const { user, role } = useAuth();
  const userGroupNames = Array.isArray(user?.groups)
    ? user.groups.map((g) => (typeof g === "object" ? g?.name : g))
    : [];
  /** Matches backend subscription add-on toggle: Admin group or superuser */
  const canMutateBilling =
    user?.is_superuser === true ||
    userGroupNames.includes("Admin") ||
    role === "Admin";

  const [subscription, setSubscription] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [checkoutFinishing, setCheckoutFinishing] = useState(false);
  const [checkoutSuccessAlert, setCheckoutSuccessAlert] = useState(false);
  const [addons, setAddons] = useState([]);
  const [usageSummary, setUsageSummary] = useState({
    active_users: 0,
    max_users: 0,
    period_start: null,
    period_end: null,
    meters: [],
    projected_usage_charges: 0,
  });
  const [confirmAddonDialog, setConfirmAddonDialog] = useState({
    open: false,
    addon: null,
    nextEnabled: false,
  });

  const fetchSubscriptionData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const requests = [
        API.get("/subscriptions/current/"),
        API.get("/subscriptions/company/"),
        API.get("/subscriptions/addons/"),
        API.get("/subscriptions/usage-summary/"),
      ];
      if (!silent) {
        setInvoicesLoading(true);
        requests.push(API.get("/subscriptions/billing/invoices/"));
      }
      const results = await Promise.all(requests);
      const [subRes, companyRes, addonsRes, usageRes, invoicesRes] = results;
      const subData = subRes.data;
      setSubscription(subData);
      setCompany(companyRes.data);
      const rows = addonsRes?.data?.results || [];
      setAddons(Array.isArray(rows) ? rows : []);
      setUsageSummary(usageRes?.data || {});
      if (invoicesRes) {
        setInvoices(invoicesRes?.data?.results || []);
      }
      return subData;
    } catch (err) {
      console.error("Error fetching subscription:", err);
      if (!silent) {
        toast.error("Failed to load subscription information");
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
        setInvoicesLoading(false);
      }
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      setInvoicesLoading(true);
      const res = await API.get("/subscriptions/billing/invoices/");
      setInvoices(res?.data?.results || []);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout !== "success" || checkoutHandledRef.current) {
      return;
    }
    checkoutHandledRef.current = true;
    setCheckoutSuccessAlert(true);
    setCheckoutFinishing(true);
    toast.success("Payment received. Updating your subscription…");

    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });

    let elapsed = 0;
    const poll = async () => {
      const subData = await fetchSubscriptionData({ silent: true });
      await fetchInvoices();
      if (subData?.stripe_subscription_id) {
        setCheckoutFinishing(false);
        toast.success("Subscription is active.");
        return;
      }
      elapsed += CHECKOUT_POLL_MS;
      if (elapsed < CHECKOUT_POLL_MAX_MS) {
        window.setTimeout(poll, CHECKOUT_POLL_MS);
      } else {
        setCheckoutFinishing(false);
      }
    };
    poll();
  }, [searchParams, setSearchParams, fetchSubscriptionData, fetchInvoices]);

  useEffect(() => {
    if (searchParams.get("checkout") === "cancel") {
      toast.info("Checkout was canceled.");
      const next = new URLSearchParams(searchParams);
      next.delete("checkout");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "past_due":
        return "warning";
      case "canceled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "active":
        return "Active";
      case "past_due":
        return "Past Due";
      case "canceled":
        return "Canceled";
      default:
        return status;
    }
  };

  const userCount = subscription?.current_user_count || 0;
  const usagePercent = 100;
  const isBillingExempt = !!subscription?.is_billing_exempt;
  const isAdtDealer = !!subscription?.is_adt_dealer;
  const adtStatus = subscription?.adt_verification_status;
  const inTrial = !!subscription?.in_trial;
  const trialDaysRemaining = subscription?.trial_days_remaining;
  const hasStripeSubscription = !!subscription?.stripe_subscription_id;
  const showSubscribeButton =
    !!subscription?.can_subscribe_checkout && !hasStripeSubscription;

  const paymentMethodSummary = () => {
    if (subscription?.payment_method_display) {
      return subscription.payment_method_display;
    }
    if (subscription?.payment_method_last4) {
      return `•••• •••• •••• ${subscription.payment_method_last4}`;
    }
    if (subscription?.payment_method_type === "link") {
      return "Stripe Link";
    }
    if (hasStripeSubscription) {
      return "Payment method on file";
    }
    return "No payment method on file";
  };

  const handleManagePayment = async () => {
    if (!canMutateBilling) {
      toast.error("Only organization administrators can manage billing.");
      return;
    }
    try {
      setUpdating(true);
      const res = await API.post("/subscriptions/billing/portal/");
      const url = res.data?.portal_url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error("Billing portal URL was not returned.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to open billing portal.");
    } finally {
      setUpdating(false);
    }
  };

  const handleSubscribeCheckout = async () => {
    if (!canMutateBilling) {
      toast.error("Only organization administrators can manage billing.");
      return;
    }
    try {
      setUpdating(true);
      const res = await API.post("/subscriptions/billing/checkout/");
      const url = res.data?.checkout_url;
      if (url) {
        window.location.href = url;
        return;
      }
      toast.error("Checkout URL was not returned.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to start checkout.");
    } finally {
      setUpdating(false);
    }
  };
  const addonByCode = (code) =>
    addons.find((row) => row?.addon_code === code || row?.addon?.code === code);

  const formatMoney = (value, decimals = 2) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  /** ISO date-only strings (YYYY-MM-DD) must not be parsed as UTC midnight. */
  const formatLocalDateOnly = (isoDate) => {
    if (!isoDate) return "—";
    const part = String(isoDate).split("T")[0];
    const [y, m, d] = part.split("-").map(Number);
    if (!y || !m || !d) return "—";
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const invoiceStatusChip = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "paid") {
      return <Chip label="Paid" size="small" color="success" sx={{ fontWeight: 600 }} />;
    }
    if (normalized === "open") {
      return <Chip label="Open" size="small" color="warning" variant="outlined" />;
    }
    return <Chip label={status || "—"} size="small" variant="outlined" />;
  };
  const formatMeterLabel = (meter) => {
    const qty = Number(meter?.quantity || 0);
    const roundedQty = Number.isInteger(qty) ? qty : Number(qty.toFixed(2));
    return `${roundedQty} ${meter?.unit_label || "units"}`;
  };

  const openAddonConfirm = (addonRow, nextEnabled) => {
    if (!canMutateBilling) {
      toast.error("Only organization administrators can change subscription add-ons.");
      return;
    }
    setConfirmAddonDialog({ open: true, addon: addonRow, nextEnabled });
  };

  const closeAddonConfirm = () => {
    if (updating) return;
    setConfirmAddonDialog({ open: false, addon: null, nextEnabled: false });
  };

  const submitAddonToggle = async () => {
    if (!canMutateBilling) {
      toast.error("Only organization administrators can change subscription add-ons.");
      return;
    }
    const addonCode = confirmAddonDialog?.addon?.addon_code;
    if (!addonCode) return;
    try {
      setUpdating(true);
      const res = await API.post(`/subscriptions/addons/${addonCode}/toggle/`, {
        enabled: confirmAddonDialog.nextEnabled,
      });
      const updated = res.data;
      if (updated?.is_billing_exempt) {
        toast.info("This organization is billing-exempt. Add-on billing controls are bypassed.");
        closeAddonConfirm();
        return;
      }
      setAddons((prev) =>
        prev.map((row) =>
          (row?.addon_code === addonCode || row?.addon?.code === addonCode)
            ? { ...row, ...updated }
            : row
        )
      );
      toast.success(
        confirmAddonDialog.nextEnabled
          ? `${updated?.addon?.name || "Add-on"} enabled`
          : `${updated?.addon?.name || "Add-on"} disabled`
      );
      closeAddonConfirm();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Unable to update add-on.";
      toast.error(detail);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading subscription information...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {!canMutateBilling && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Only organization administrators can change subscription and billing settings.
          </Alert>
        )}
        {checkoutSuccessAlert && (
          <Alert
            severity={checkoutFinishing ? "info" : "success"}
            sx={{ mb: 2 }}
            onClose={() => setCheckoutSuccessAlert(false)}
          >
            {checkoutFinishing
              ? "Subscription active — billing details are updating. This may take a moment."
              : "Subscription active. Your billing details are up to date."}
          </Alert>
        )}
        {/* Header Card - Matching AddUserPage style */}
        <Card
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={{ xs: 2, md: 0 }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <CreditCardIcon sx={{ fontSize: 32, color: "primary.main" }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Account & Subscription
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your subscription, billing, and user limits
                </Typography>
              </Box>
            </Box>
            {subscription?.status === "active" && (
              <Chip
                icon={<CheckCircleIcon />}
                label={getStatusLabel(subscription.status)}
                color={getStatusColor(subscription.status)}
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
        </Card>

        {/* Main Grid - 3 Column Layout (matching AddUserPage) */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
          gap={3}
        >
          {/* First Card - Subscription Overview */}
          <Card
            data-tour="subscription-plan"
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Typography fontWeight={700} mb={2}>
                Subscription Overview
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Status
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip
                      label={getStatusLabel(subscription?.status)}
                      color={getStatusColor(subscription?.status)}
                      size="small"
                    />
                    {!isBillingExempt && subscription?.trial_ends_at && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color={inTrial ? "info" : "default"}
                        label={
                          inTrial
                            ? `Trial: ${trialDaysRemaining} day${trialDaysRemaining === 1 ? "" : "s"} left`
                            : "Trial ended"
                        }
                      />
                    )}
                  </Stack>
                </Box>

                {isAdtDealer && !isBillingExempt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      ADT dealer
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      {adtStatus === "verified" ? (
                        <Chip
                          size="small"
                          color="primary"
                          label="ADT Verified"
                          avatar={
                            <Avatar
                              sx={{
                                bgcolor: "primary.main",
                                width: 22,
                                height: 22,
                              }}
                            >
                              <CheckCircleIcon sx={{ fontSize: 14, color: "#fff" }} />
                            </Avatar>
                          }
                        />
                      ) : (
                        <>
                          <Chip size="small" variant="outlined" label="Pending" />
                          <Tooltip
                            title="Territory CRM verifies ADT authorized dealer numbers manually. After verification, you can subscribe at $59/month when your trial ends."
                            arrow
                          >
                            <HelpOutlineIcon
                              fontSize="small"
                              color="action"
                              sx={{ cursor: "help" }}
                              aria-label="ADT verification help"
                            />
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                    {subscription?.adt_dealer_number && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                        Dealer #{subscription.adt_dealer_number}
                      </Typography>
                    )}
                  </Box>
                )}

                {subscription?.can_subscribe_checkout && (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!canMutateBilling || updating}
                    onClick={handleSubscribeCheckout}
                  >
                    Subscribe — $59/month
                  </Button>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Plan
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {subscription?.plan_label ||
                      `$${subscription?.monthly_rate_per_user || 20}/user/month`}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Next invoice
                  </Typography>
                  <Typography variant="body2">
                    {formatLocalDateOnly(subscription?.next_billing_date)}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Monthly Amount
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${formatMoney(subscription?.current_monthly_amount ?? 0, 2)}/month
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Second Card - Usage Display */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography fontWeight={700}>Usage Display</Typography>
                <PeopleIcon color="action" />
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      Active Users: <strong>{userCount}</strong>
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(usagePercent, 100)}
                    color="primary"
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Monthly Metered Usage
                  </Typography>
                  {(usageSummary?.meters || []).map((meter) => (
                    <Box key={meter.addon_code} sx={{ mb: 1.5 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                        <Typography variant="body2">{meter.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatMeterLabel(meter)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(Number(meter.quantity || 0), 100)}
                        sx={{ height: 6, borderRadius: 1, mb: 0.25 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        ${formatMoney(meter.projected_amount, 2)} projected (${formatMoney(meter.unit_price, 2)}/{meter.unit_label})
                      </Typography>
                    </Box>
                  ))}
                  <Typography variant="body2" fontWeight={700} sx={{ mt: 1 }}>
                    Projected Usage Charges: ${formatMoney(usageSummary?.projected_usage_charges || 0, 2)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Third Card - Billing Information */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Typography fontWeight={700} mb={2}>
                Billing Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Company Name
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {company?.name || "—"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Billing Email
                  </Typography>
                  <Typography variant="body2">
                    {company?.billing_email || "—"}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Payment Method
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PaymentIcon color="action" fontSize="small" />
                    <Typography variant="body2">{paymentMethodSummary()}</Typography>
                  </Stack>
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleManagePayment}
                  disabled={
                    !canMutateBilling ||
                    updating ||
                    isBillingExempt ||
                    !subscription?.stripe_configured
                  }
                  fullWidth
                  size="small"
                >
                  Update payment method
                </Button>

                {!isBillingExempt && (
                  <>
                    <Divider />
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      size="small"
                      disabled={
                        !canMutateBilling ||
                        updating ||
                        !hasStripeSubscription
                      }
                      title={
                        hasStripeSubscription
                          ? "Cancel via Stripe billing portal (coming soon)"
                          : "Available after an active Stripe subscription"
                      }
                    >
                      Cancel subscription
                    </Button>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Add-Ons Section */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mt: 5, mb: 2 }}
          color="text.primary"
        >
          Features & Add-Ons
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
          gap={3}
        >
          {isBillingExempt && (
            <Alert severity="info" sx={{ gridColumn: { xs: "1", md: "1 / -1" } }}>
              Billing controls are bypassed for this organization. Add-on toggles are disabled.
            </Alert>
          )}
          {/* ---------------------------------------------------------------------------------------Email - Included */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(25,118,210,0.08)",
                    }}
                  >
                    <EmailIcon color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>Email Integration</Typography>
                    <Typography variant="body2" color="text.secondary">
                      SMTP notifications
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label="Included"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="h6" fontWeight={700} color="success.main">
                $0.00
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Free with your plan
              </Typography>
              <Button
                component={Link}
                to="/settings/integrations"
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                sx={{ textTransform: "none" }}
              >
                View Settings
              </Button>
            </CardContent>
          </Card>

          {/* -----------------------------------------------------------------------------------------SMS (Twilio) - Add-on */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              {(() => {
                const smsAddon = addonByCode("sms-messages");
                const smsEnabled = !!smsAddon?.is_enabled;
                return (
                  <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(25,118,210,0.08)",
                    }}
                  >
                    <SmsIcon color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>SMS Integration</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Two-way SMS with leads
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={smsEnabled ? "Enabled" : "Add-on"}
                    color={smsEnabled ? "success" : "default"}
                    size="small"
                    variant={smsEnabled ? "filled" : "outlined"}
                  />
                </Stack>
              </Box>
              <Typography variant="body1" fontWeight={700}>
                ${formatMoney(smsAddon?.addon?.monthly_price, 2)}/mo
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                + ${formatMoney(smsAddon?.addon?.unit_price, 2)} per {smsAddon?.addon?.usage_unit_label || "unit"}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={smsEnabled ? "outlined" : "contained"}
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    if (!smsAddon) return;
                    openAddonConfirm(smsAddon, !smsEnabled);
                  }}
                  disabled={isBillingExempt || !smsAddon || !canMutateBilling}
                >
                  {smsEnabled ? "Disable Add-on" : "Enable Add-on"}
                </Button>
                {smsEnabled && (
                  <Button
                    component={Link}
                    to="/settings/integrations"
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    View Settings
                  </Button>
                )}
              </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* ----------------------------------------------------------------------------------------------------AI Sales Bot - Feature */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              {(() => {
                const botAddon = addonByCode("ai-sales-bot-basic");
                const botEnabled = !!botAddon?.is_enabled;
                return (
                  <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(25,118,210,0.08)",
                    }}
                  >
                    <PsychologyIcon color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>
                      AI Sales Bot
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      AI assistant for objection handling and scripts
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={botEnabled ? "Enabled" : "Feature"}
                    color={botEnabled ? "success" : "default"}
                    size="small"
                    variant={botEnabled ? "filled" : "outlined"}
                  />
                </Stack>
              </Box>
              <Typography variant="body1" fontWeight={700}>
                ${formatMoney(botAddon?.addon?.monthly_price, 2)}/month
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Flat monthly feature
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={botEnabled ? "outlined" : "contained"}
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    if (!botAddon) return;
                    openAddonConfirm(botAddon, !botEnabled);
                  }}
                  disabled={isBillingExempt || !botAddon || !canMutateBilling}
                >
                  {botEnabled ? "Disable Feature" : "Enable Feature"}
                </Button>
                {botEnabled && (
                  <Button
                    component={Link}
                    to="/follow-up-intelligence"
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    View Settings
                  </Button>
                )}
              </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* ----------------------------------------------------------------------------------------------------AI Sales Closer - Feature */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              {(() => {
                const closerAddon = addonByCode("ai-sales-bot-closer");
                const closerEnabled = !!closerAddon?.is_enabled;
                return (
                  <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(25,118,210,0.08)",
                    }}
                  >
                    <TrendingUpIcon color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>
                      AI Sales Closer
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Advanced close coaching and automation
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={closerEnabled ? "Enabled" : "Feature"}
                    color={closerEnabled ? "success" : "default"}
                    size="small"
                    variant={closerEnabled ? "filled" : "outlined"}
                  />
                </Stack>
              </Box>
              <Typography variant="body1" fontWeight={700}>
                ${formatMoney(closerAddon?.addon?.monthly_price, 2)}/month
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Flat monthly feature
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={closerEnabled ? "outlined" : "contained"}
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    if (!closerAddon) return;
                    openAddonConfirm(closerAddon, !closerEnabled);
                  }}
                  disabled={isBillingExempt || !closerAddon || !canMutateBilling}
                >
                  {closerEnabled ? "Disable Feature" : "Enable Feature"}
                </Button>
                {closerEnabled && (
                  <Button
                    component={Link}
                    to="/follow-up-intelligence"
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    View Settings
                  </Button>
                )}
              </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* ----------------------------------------------------------------------------------------------------Lead Enrichment - Add-on */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              {(() => {
                const enrichAddon = addonByCode("lead-intel");
                const enrichEnabled = !!enrichAddon?.is_enabled;
                return (
                  <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(25,118,210,0.08)",
                    }}
                  >
                    <PersonAddIcon color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>
                      Lead Enrichment
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Enrich phone numbers and emails
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={enrichEnabled ? "Enabled" : "Add-on"}
                    color={enrichEnabled ? "success" : "default"}
                    size="small"
                    variant={enrichEnabled ? "filled" : "outlined"}
                  />
                </Stack>
              </Box>
              <Typography variant="body1" fontWeight={700}>
                ${formatMoney(enrichAddon?.addon?.unit_price, 2)} per {enrichAddon?.addon?.usage_unit_label || "record"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Usage-based add-on
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={enrichEnabled ? "outlined" : "contained"}
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    if (!enrichAddon) return;
                    openAddonConfirm(enrichAddon, !enrichEnabled);
                  }}
                  disabled={isBillingExempt || !enrichAddon || !canMutateBilling}
                >
                  {enrichEnabled ? "Disable Add-on" : "Enable Add-on"}
                </Button>
              </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* ----------------------------------------------------------------------------------------------------New Homeowners List - Add-on */}
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              {(() => {
                const homeownersAddon = addonByCode("lead-data");
                const homeownersEnabled = !!homeownersAddon?.is_enabled;
                return (
                  <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(25,118,210,0.08)",
                    }}
                  >
                    <PersonAddIcon color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography fontWeight={700}>
                      New Homeowners List
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Weekly homeowner lead list deliveries
                    </Typography>
                  </Box>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={homeownersEnabled ? "Enabled" : "Add-on"}
                    color={homeownersEnabled ? "success" : "default"}
                    size="small"
                    variant={homeownersEnabled ? "filled" : "outlined"}
                  />
                </Stack>
              </Box>
              <Typography variant="body1" fontWeight={700}>
                ${formatMoney(homeownersAddon?.addon?.unit_price, 2)} per {homeownersAddon?.addon?.usage_unit_label || "lead"}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Usage-based add-on
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={homeownersEnabled ? "outlined" : "contained"}
                  size="small"
                  sx={{ textTransform: "none" }}
                  onClick={() => {
                    if (!homeownersAddon) return;
                    openAddonConfirm(homeownersAddon, !homeownersEnabled);
                  }}
                  disabled={isBillingExempt || !homeownersAddon || !canMutateBilling}
                >
                  {homeownersEnabled ? "Disable Add-on" : "Enable Add-on"}
                </Button>
              </Stack>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </Box>

        {!isBillingExempt && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 3,
              bgcolor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (t) =>
                t.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (t) =>
                t.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Typography fontWeight={700}>Billing History</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Your recent invoices and payments
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {invoicesLoading && !invoices.length ? (
                <Typography variant="body2" color="text.secondary">
                  Loading invoices…
                </Typography>
              ) : invoices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No invoices yet. Invoices appear here after your first payment.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="right" padding="checkbox" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((row) => {
                        const downloadUrl = row.invoice_pdf || row.hosted_invoice_url;
                        return (
                          <TableRow key={row.id}>
                            <TableCell>{formatLocalDateOnly(row.date)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {row.description}
                              </Typography>
                            </TableCell>
                            <TableCell>{invoiceStatusChip(row.status)}</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                ${formatMoney(row.amount, 2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" padding="checkbox">
                              {downloadUrl ? (
                                <IconButton
                                  size="small"
                                  aria-label="Download invoice"
                                  href={downloadUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  component="a"
                                >
                                  <FileDownloadOutlinedIcon fontSize="small" />
                                </IconButton>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog open={confirmAddonDialog.open} onClose={closeAddonConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>
          {confirmAddonDialog.nextEnabled ? "Enable add-on?" : "Disable add-on?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmAddonDialog.nextEnabled
              ? `You are enabling ${confirmAddonDialog?.addon?.addon?.name || "this add-on"}. Charges will apply based on pricing rules.`
              : `You are disabling ${confirmAddonDialog?.addon?.addon?.name || "this add-on"}. New usage will stop after confirmation.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddonConfirm} disabled={updating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitAddonToggle} disabled={updating || !canMutateBilling}>
            {updating ? "Saving..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}